import {Banner} from '@primer/react'
import {type RefObject, useCallback, useState} from 'react'

import {BUTTON_LABELS, LABELS} from '../../constants'

interface SavedViewMutationErrorBannerProps {
  message: string | null
  onRetry?: () => void
  isRetrying?: boolean
  /**
   * Whether Try again is unavailable. It stays focusable and does nothing when activated.
   * @default false
   */
  retryDisabled?: boolean
  flush?: boolean
  focusOnMount?: boolean
  returnFocusRef?: RefObject<HTMLElement | null>
}

export function SavedViewMutationErrorBanner({
  message,
  onRetry,
  isRetrying = false,
  retryDisabled = false,
  flush,
  focusOnMount = true,
  returnFocusRef,
}: SavedViewMutationErrorBannerProps) {
  const bannerRef = useCallback(
    (node: HTMLElement | null) => {
      if (!node) return
      if (focusOnMount) node.focus({preventScroll: true})
      return () => {
        if (node.contains(node.ownerDocument.activeElement)) {
          returnFocusRef?.current?.focus({preventScroll: true})
        }
      }
    },
    [focusOnMount, returnFocusRef],
  )
  // Keep the last message visible while a retry is in flight so focus stays
  // on "Try again".
  const [stickyMessage, setStickyMessage] = useState<string | null>(message)
  const nextMessage = message ?? (isRetrying ? stickyMessage : null)
  if (nextMessage !== stickyMessage) setStickyMessage(nextMessage)

  if (!stickyMessage) return null

  return (
    <Banner
      ref={bannerRef}
      variant="critical"
      title={stickyMessage}
      flush={flush}
      actionsLayout={onRetry ? 'inline' : undefined}
      primaryAction={
        onRetry ? (
          <Banner.PrimaryAction
            onClick={retryDisabled ? undefined : onRetry}
            // Not `disabled`: a focused button that turns disabled drops focus to the page.
            {...(retryDisabled ? {'aria-disabled': true} : {})}
            loading={isRetrying}
            loadingAnnouncement={LABELS.views.retrying}
          >
            {BUTTON_LABELS.tryAgain}
          </Banner.PrimaryAction>
        ) : undefined
      }
    />
  )
}
